import { collection, doc, getDoc, getDocs, limit, query } from 'firebase/firestore';
import { CompositeFieldType } from '#types/System.types';
import { CUSTOM_COMPONENTS_LIMIT, RecordStatus } from '#types/System.Parameters.types';
import { firestore } from '../../services/startFirestore';

/** Firestore collection for composite field type documents. */
const COLLECTION = 'compositeFieldTypes';

/** Built-in composite field type for SellPriceUI (not stored in Firestore). */
const SELL_PRICE_UI_COMPOSITE: CompositeFieldType = {
    docId: 'sellPriceUI',
    defaultUILayout: { structure: {} } as unknown as CompositeFieldType['defaultUILayout'],
    status: RecordStatus.Active,
};

/** In-memory cache of fetched composite field types (keyed by docId). */
const cache = new Map<string, CompositeFieldType>();
let allFetchedPromise: Promise<CompositeFieldType[]> | null = null;
const invalidationListeners = new Set<() => void>();

/**
 * Fetches all composite field types, caches them, and returns the array.
 * Results are cached until explicitly invalidated via `invalidateCompositeFieldTypeCache`.
 */
export const fetchAllCompositeFieldTypes = (): Promise<CompositeFieldType[]> => {
    if (!allFetchedPromise) {
        allFetchedPromise = getDocs(
            query(collection(firestore, COLLECTION), limit(CUSTOM_COMPONENTS_LIMIT))
        ).then(snapshot => {
            const results: CompositeFieldType[] = [];
            for (const docSnap of snapshot.docs) {
                const data = { docId: docSnap.id, ...docSnap.data() } as CompositeFieldType;
                cache.set(docSnap.id, data);
                results.push(data);
            }
            if (!cache.has(SELL_PRICE_UI_COMPOSITE.docId!)) {
                cache.set(SELL_PRICE_UI_COMPOSITE.docId!, SELL_PRICE_UI_COMPOSITE);
                results.push(SELL_PRICE_UI_COMPOSITE);
            }
            return results;
        });
    }
    return allFetchedPromise;
};

/** Clears the cache so the next `fetchAllCompositeFieldTypes` call re-fetches from Firestore. */
export const invalidateCompositeFieldTypeCache = (): void => {
    cache.clear();
    allFetchedPromise = null;
    for (const listener of invalidationListeners) listener();
};

/**
 * Subscribes to cache invalidation events.
 * @returns Unsubscribe function.
 */
export const onCompositeFieldTypeCacheInvalidated = (listener: () => void): (() => void) => {
    invalidationListeners.add(listener);
    return () => { invalidationListeners.delete(listener); };
};

/** Synchronous read from the in-memory cache. Returns null if not yet fetched. */
export const getCompositeFieldTypeCached = (id: string): CompositeFieldType | null => {
    return cache.get(id) ?? null;
};

/**
 * Gets a single composite field type by ID.
 * Checks the cache first; fetches individually if not cached.
 */
export const getCompositeFieldType = async (id: string): Promise<CompositeFieldType | null> => {
    const cached = cache.get(id);
    if (cached) return cached;

    const docSnap = await getDoc(doc(firestore, COLLECTION, id));
    if (!docSnap.exists()) return null;

    const data = { docId: docSnap.id, ...docSnap.data() } as CompositeFieldType;
    cache.set(id, data);
    return data;
};
